// Libs
import _find from 'lodash/find'
import _map from 'lodash/map'

// Utils
import traverse from 'fc-utils/traverse'
import {
  getHeftyCropConversionFactor,
} from 'hfty-utils'

// Constants
import { PROP_US_CUSTOMARY } from 'fc-model-users'
import {
  PROP_YIELD_TARGET,
  PROP_REMOVAL_YIELD,
  PROP_REMOVAL_CROP,
  PROP_CROP,
  RECOMMENDATION_FERTILIZER_SET,
  // Recommendation Fertilizer Properties
  PROP_COST_PER_TON,
  PROP_COST_PER_ACRE,
  PROP_MAX_RATE,
  PROP_MIN_RATE,
  PROP_FERTILIZER,
  PROP_ADJUSTMENT_VALUE,
  PROP_ADJUSTMENT_TYPE,
  ADJ_PERCENT,
  ADJ_PRODUCT_RATE,
  ADJ_ELEMENT,
} from 'hfty-model-recommendations'

import {
  PROP_STATE,
  SOLID,
} from 'fc-model-fertilizers'

import {
  KG_PER_TON,
  KG_PER_LB,
  L_PER_GAL,
  HA_PER_AC,
} from 'hfty'

import {
  PROP_CROP_ID,
} from 'fc-model-crops'

function convertAdjustmentTypeFrontdnd (type, value, unitSystem, fert_state) {
  switch (type) {
    case ADJ_PERCENT:
      return value
    case ADJ_PRODUCT_RATE:
      return unitSystem === PROP_US_CUSTOMARY
        ? value
        : (fert_state === SOLID ? value * KG_PER_LB : value * L_PER_GAL)
    case ADJ_ELEMENT:
      return unitSystem === PROP_US_CUSTOMARY ? value : value * (KG_PER_LB / HA_PER_AC)
  }
}

function convertAdjustmentTypeBackend (type, value, unitSystem, fert_state) {
  switch (type) {
    case ADJ_PERCENT:
      return value
    case ADJ_PRODUCT_RATE:
      return unitSystem === PROP_US_CUSTOMARY
        ? value
        : (fert_state === SOLID ? value / KG_PER_LB : value / L_PER_GAL)
    case ADJ_ELEMENT:
      return unitSystem === PROP_US_CUSTOMARY ? value : value / (KG_PER_LB / HA_PER_AC)
  }
}

export function convertRecommendationToFrontendUnits (model, crops, unitSystem, fertilizers) {
  const crop = _find(crops, (crop): boolean => (
    crop[PROP_CROP_ID] === model[PROP_CROP])
  ) || {}

  const removalCrop = _find(crops, (crop): boolean => (
    crop[PROP_CROP_ID] === model[PROP_REMOVAL_CROP])
  ) || {}

  const conversionFactorCrop = getHeftyCropConversionFactor(crop, unitSystem)
  const conversionFactorRemovalCrop = getHeftyCropConversionFactor(removalCrop, unitSystem)

  return {
    ...model,
    [PROP_YIELD_TARGET]: (model[PROP_YIELD_TARGET] != null && model[PROP_YIELD_TARGET] !== ''
      ? Number(model[PROP_YIELD_TARGET]) * conversionFactorCrop : null),
    [PROP_REMOVAL_YIELD]: (model[PROP_REMOVAL_YIELD] != null && model[PROP_REMOVAL_YIELD] !== ''
      ? Number(model[PROP_REMOVAL_YIELD]) * conversionFactorRemovalCrop : null),
    [RECOMMENDATION_FERTILIZER_SET]: [
      ..._map(model[RECOMMENDATION_FERTILIZER_SET], (recom_fert: Object): Object => {
        const state = traverse([recom_fert[PROP_FERTILIZER], PROP_STATE])(fertilizers)
        return ({
          ...recom_fert,
          [PROP_COST_PER_ACRE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_COST_PER_ACRE] : recom_fert[PROP_COST_PER_ACRE] * HA_PER_AC),
          [PROP_COST_PER_TON]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_COST_PER_TON] : recom_fert[PROP_COST_PER_TON] * KG_PER_TON),
          [PROP_MAX_RATE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_MAX_RATE]
            : (state === SOLID ? recom_fert[PROP_MAX_RATE] * KG_PER_LB : recom_fert[PROP_MAX_RATE] * L_PER_GAL)),
          [PROP_MIN_RATE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_MIN_RATE]
            : (state === SOLID ? recom_fert[PROP_MIN_RATE] * KG_PER_LB : recom_fert[PROP_MIN_RATE] * L_PER_GAL)),
          [PROP_ADJUSTMENT_VALUE]: convertAdjustmentTypeFrontdnd(
            recom_fert[PROP_ADJUSTMENT_TYPE],
            recom_fert[PROP_ADJUSTMENT_VALUE],
            unitSystem,
            state
          ),
        })
      }),
    ],
  }
}

export function convertRecommendationToBackendUnits (model, crops, unitSystem, fertilizers) {
  const crop = _find(crops, (crop): boolean => (
    crop[PROP_CROP_ID] === model[PROP_CROP])
  ) || {}

  const removalCrop = _find(crops, (crop): boolean => (
    crop[PROP_CROP_ID] === model[PROP_REMOVAL_CROP])
  ) || {}
  const conversionFactorCrop = getHeftyCropConversionFactor(crop, unitSystem)
  const conversionFactorRemovalCrop = getHeftyCropConversionFactor(removalCrop, unitSystem)

  return {
    ...model,
    [PROP_YIELD_TARGET]: (model[PROP_YIELD_TARGET] != null && model[PROP_YIELD_TARGET] !== ''
      ? Number(model[PROP_YIELD_TARGET]) / conversionFactorCrop : null),
    [PROP_REMOVAL_YIELD]: (model[PROP_REMOVAL_YIELD] != null && model[PROP_REMOVAL_YIELD] !== ''
      ? Number(model[PROP_REMOVAL_YIELD]) / conversionFactorRemovalCrop : null),
    [RECOMMENDATION_FERTILIZER_SET]: [
      ..._map(model[RECOMMENDATION_FERTILIZER_SET], (recom_fert: Object): Object => {
        const state = traverse([recom_fert[PROP_FERTILIZER], PROP_STATE])(fertilizers)
        return ({
          ...recom_fert,
          [PROP_COST_PER_ACRE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_COST_PER_ACRE] : recom_fert[PROP_COST_PER_ACRE] / HA_PER_AC),
          [PROP_COST_PER_TON]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_COST_PER_TON] : recom_fert[PROP_COST_PER_TON] / KG_PER_TON),
          [PROP_MAX_RATE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_MAX_RATE]
            : (state === SOLID ? recom_fert[PROP_MAX_RATE] / KG_PER_LB : recom_fert[PROP_MAX_RATE] / L_PER_GAL)),
          [PROP_MIN_RATE]: (unitSystem === PROP_US_CUSTOMARY
            ? recom_fert[PROP_MIN_RATE]
            : (state === SOLID ? recom_fert[PROP_MIN_RATE] / KG_PER_LB : recom_fert[PROP_MIN_RATE] / L_PER_GAL)),
          [PROP_ADJUSTMENT_VALUE]: convertAdjustmentTypeBackend(
            recom_fert[PROP_ADJUSTMENT_TYPE],
            recom_fert[PROP_ADJUSTMENT_VALUE],
            unitSystem,
            state
          ),
        })
      }),
    ],
  }
}
